<template>
  <li v-if="shouldShow" class="mobile-nav-item" :class="{ 'has-children': hasChildren }">
    <!-- Link -->
    <component
      :is="linkComponent"
      :to="computedUrl"
      :href="computedUrl"
      :target="item.openInNewTab ? '_blank' : '_self'"
      :rel="item.openInNewTab ? 'noopener noreferrer' : ''"
      class="mobile-nav-link"
      :class="customClasses"
      @click="handleClick"
    >
      <i v-if="item.icon" :class="item.icon + ' me-2'"></i>
      {{ displayLabel }}
      <i v-if="hasChildren" class="bi bi-chevron-down ms-auto submenu-arrow" :class="{ rotated: childrenOpen }"></i>
    </component>

    <!-- Children (Submenu) -->
    <ul v-if="hasChildren" class="mobile-submenu" :class="{ open: childrenOpen }">
      <nav-menu-item-mobile
        v-for="child in item.children"
        :key="child.id"
        :item="child"
        :is-logged-in="isLoggedIn"
        :is-admin="isAdmin"
        :depth="depth + 1"
        :menu-pages="menuPages"
        @close="$emit('close')"
      />
    </ul>
  </li>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { MenuItemData } from '@/components/admin/MenuBuilder.vue'
import {
  resolveMenuItemLabel,
  resolveMenuItemUrl,
  type MenuLookupPage,
} from '@/utils/menuBuilderHelpers'

interface Props {
  item: MenuItemData
  isLoggedIn: boolean
  isAdmin: boolean
  depth?: number
  menuPages?: MenuLookupPage[]
}

const props = withDefaults(defineProps<Props>(), {
  depth: 0,
  menuPages: () => [],
})

const emit = defineEmits<{
  close: []
}>()

const childrenOpen = ref(false)

const hasChildren = computed(() => {
  return props.item.children && props.item.children.length > 0
})

const shouldShow = computed(() => {
  if (!props.item.visible) return false
  
  switch (props.item.visibility) {
    case 'logged-in':
      return props.isLoggedIn
    case 'logged-out':
      return !props.isLoggedIn
    case 'admin':
      return props.isAdmin
    default:
      return true
  }
})

const linkComponent = computed(() => {
  if (props.item.type === 'custom' && props.item.url?.startsWith('http')) {
    return 'a'
  }
  return 'router-link'
})

const computedUrl = computed(() => {
  return resolveMenuItemUrl(props.item, props.menuPages)
})

const displayLabel = computed(() => {
  return resolveMenuItemLabel(props.item, props.menuPages)
})

const customClasses = computed(() => {
  if (!props.item.cssClass) return {}
  return props.item.cssClass.split(' ').reduce((acc, className) => {
    acc[className] = true
    return acc
  }, {} as Record<string, boolean>)
})

function handleClick(event: Event) {
  if (hasChildren.value) {
    event.preventDefault()
    childrenOpen.value = !childrenOpen.value
  } else {
    emit('close')
  }
}
</script>

<style scoped>
.mobile-nav-item.has-children {
  border-left: 3px solid #3498db;
}

.mobile-nav-link {
  display: flex;
  align-items: center;
  padding: 0.875rem 1rem;
  color: inherit;
  text-decoration: none;
  transition: all 0.2s;
}

.mobile-nav-link:hover {
  background: rgba(255, 255, 255, 0.1);
}

.submenu-arrow {
  font-size: 0.875rem;
  transition: transform 0.3s ease;
}

.submenu-arrow.rotated {
  transform: rotate(180deg);
}

.mobile-submenu {
  list-style: none;
  padding: 0;
  margin: 0;
  background: rgba(0, 0, 0, 0.1);
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.mobile-submenu.open {
  max-height: 500px;
}

.mobile-submenu .mobile-nav-link {
  padding-left: 2rem;
  font-size: 0.925rem;
}
</style>
